import { create } from 'zustand';

type FilterStore = {
  /** Current filter query */
  query: string;
  /** Currently selected mine site code (defaults to 'all') */
  selectedSite: string | null;
  /** Whether to filter blasts with optimisations (true), without optimisations (false), or not apply this filter (null) */
  hasOptimisation: boolean | null;
  /** Whether to filter blasts that have drill domains (true), do not have drill domains (false), or not apply this filter (null) */
  hasDrillDomains: boolean | null;
  /** Whether to filter blasts that need attention (true), have no issues (false), or not apply this filter (null) */
  needsAttention: boolean | null;
  /** Set the filter query */
  setQuery: (query: string) => void;
  /** Set the selected mine site */
  setSelectedSite: (site: string | null) => void;
  /** Set whether to filter blasts with optimisations (true), without optimisations (false), or not apply this filter (null) */
  setHasOptimisation: (hasOptimisation: boolean | null) => void;
  /** Set whether to filter blasts with drill domains (true), without drill domains (false), or not apply this filter (null) */
  setHasDrillDomains: (hasDrillDomains: boolean | null) => void;
  /** Set whether to filter blasts that need attention (true), have no issues (false), or not apply this filter (null) */
  setNeedsAttention: (needsAttention: boolean | null) => void;
  /** Clear the filter query (reset to empty) */
  clearQuery: () => void;
  /** Reset dropdown filters only (mine site, optimisation, drill domains, needs attention) — does not affect the search query */
  resetDropdownFilters: () => void;
  /**
   * Reset all filters to their initial state, including the search query.
   * Used when the empty state of the blast list is shown and the user clicks "Clear Filters" to reset everything.
   */
  resetFilters: () => void;
};

const useFilterStore = create<FilterStore>((set) => ({
  query: '',

  selectedSite: 'all',

  hasOptimisation: null,

  hasDrillDomains: null,

  needsAttention: null,

  setQuery: (query: string) => set({ query }),

  setSelectedSite: (site: string | null) => set({ selectedSite: site }),

  setHasOptimisation: (hasOptimisation: boolean | null) => set({ hasOptimisation }),

  setHasDrillDomains: (hasDrillDomains: boolean | null) => set({ hasDrillDomains }),

  setNeedsAttention: (needsAttention: boolean | null) => set({ needsAttention }),

  clearQuery: () => set({ query: '' }),

  resetDropdownFilters: () =>
    set({ selectedSite: 'all', hasOptimisation: null, hasDrillDomains: null, needsAttention: null }),

  resetFilters: () =>
    set({ query: '', selectedSite: 'all', hasOptimisation: null, hasDrillDomains: null, needsAttention: null }),
}));

/** Derived selector: resolves the active mine code from selectedSite, returning undefined when 'all' or null */
const selectActiveMineCode = (state: FilterStore): string | undefined =>
  state.selectedSite && state.selectedSite !== 'all' ? state.selectedSite : undefined;

export { selectActiveMineCode, useFilterStore };
